/**
 * For You (/): every feed the reader shows in For You built at once by one
 * feeds v2 request and mixed by the server (`for_you_mix.combine`, a first
 * approximation for now). One streamed request per load
 * (`GET /api/v1/feeds/for-you/stream`). Wire types and the pure state the page
 * renders.
 *
 * The stream sends the reader's last delivered For You first (`delivered`),
 * then `update`s: each carries the stories new or changed since they were last
 * sent, and the whole order by id. While the load runs, only the stories at the
 * top of each sort (`HEAD_STORIES`) and changes to stories already sent are
 * sent; the rest arrive once, as a
 * continuation of the last update, just before `done`. A story the page
 * already has (sent earlier, or delivered) comes as a patch: its id and the
 * fields that changed (`patches`), merged into the page's copy; an unchanged
 * delivered story is not sent again, and the order names it. The page shows the
 * stories of the latest order it has been sent; while the load runs, delivered
 * stories it has not ranked yet follow below, so the page never empties while
 * feeds build. Once it finishes, delivered stories stay
 * only for feeds whose build failed, or for all of For You when no feed
 * finished (an outage).
 *
 * Each ranking is shown as it arrives.
 */
import type { RankedStory } from './feedsV2';

export type ForYouV2Story = RankedStory;

/** The fields of a story the page already has that changed since it was sent. */
export type ForYouV2Patch = Partial<ForYouV2Story> & { id: string };

export interface ForYouV2Feed {
  search_id: string;
  name: string;
  status: 'building' | 'done' | 'failed';
  /** Stories it ranked (done), or why it failed. */
  stories?: number;
  detail?: string | null;
}

/** One line of the stream (`application/x-ndjson`). */
export type ForYouV2Event =
  | { type: 'feeds'; window_hours: number; feeds: { search_id: string; name: string }[] }
  | { type: 'delivered'; delivered_at: string; stories: ForYouV2Story[]; append?: false }
  | { type: 'delivered'; stories: ForYouV2Story[]; append: true }
  | { type: 'update'; stories: ForYouV2Story[]; patches?: ForYouV2Patch[]; order: string[]; provisional: boolean; append?: false }
  | { type: 'update'; stories: ForYouV2Story[]; patches?: ForYouV2Patch[]; append: true }
  | { type: 'feed_done'; search_id: string; stories: number }
  | { type: 'feed_error'; search_id: string; detail: string | null }
  | { type: 'heartbeat' }
  | { type: 'done' }
  | { type: 'error'; detail?: string };

export interface ForYouV2State {
  /** The feeds being built; null until the stream names them. */
  feeds: ForYouV2Feed[] | null;
  /** What the page shows: this load's order, then any delivered stories still shown (`fromDelivered`). */
  stories: ForYouV2Story[];
  /** Some feed's stories are still its caches' alone. */
  provisional: boolean;
  done: boolean;
  error: string | null;
  /** Every story this load sent, by id, and its latest order (which may name a delivered story it did not resend). */
  bodies: Record<string, ForYouV2Story>;
  order: string[];
  /** The reader's last delivered For You, if the stream sent one. */
  delivered: { at: string; stories: ForYouV2Story[] } | null;
  /** How many of `stories` (the last ones) come from the delivered For You rather than this load. */
  fromDelivered: number;
}

export const INITIAL_FOR_YOU_V2: ForYouV2State = {
  feeds: null, stories: [], provisional: false, done: false, error: null,
  bodies: {}, order: [], delivered: null, fromDelivered: 0,
};

/**
 * Everything that names a story: its face, its alternatives and its topic. A delivered story is one this
 * load ranked when they share a name, not only a topic: a one-member story has no topic until a second
 * article joins it, so the delivery may name by its face a story this load ranks by its new topic
 * (PRO-1164; the server's `for_you_mix.story_keys`).
 */
const names = (story: ForYouV2Story) => [
  `article:${story.id}`, ...(story.alternatives ?? []).map(alternative => `article:${alternative.id}`),
  ...(story.topic_id ? [`topic:${story.topic_id}`] : []),
];

/** The stories to show: this load's order, then the delivered stories it has not ranked (see the module doc). */
export function shownStories(state: ForYouV2State): Pick<ForYouV2State, 'stories' | 'fromDelivered'> {
  const delivered = deliveredById(state);
  const fresh = state.order.map(id => state.bodies[id] ?? delivered.get(id))
    .filter((story): story is ForYouV2Story => !!story);
  if (!state.delivered) return { stories: fresh, fromDelivered: 0 };
  // Match the server's transitive grouping, including links through delivered stories.
  const parent = new Map<string, string>();
  const root = (name: string): string => {
    if (!parent.has(name)) parent.set(name, name);
    let node = name;
    while (parent.get(node) !== node) node = parent.get(node)!;
    return node;
  };
  const allNames = [...fresh, ...state.delivered.stories].map(names);
  for (const [first, ...rest] of allNames) {
    for (const name of rest) parent.set(root(name), root(first));
  }
  const ranked = new Set(allNames.slice(0, fresh.length).map(([first]) => root(first)));
  let remnants = state.delivered.stories.filter((_, index) =>
    !ranked.has(root(allNames[fresh.length + index][0])));
  const feeds = state.feeds ?? [];
  if (state.done && feeds.some(feed => feed.status === 'done')) {
    const failed = new Set(feeds.filter(feed => feed.status === 'failed').map(feed => feed.search_id));
    remnants = remnants.filter(story => (story.for_you_searches ?? []).some(feed => failed.has(feed.id)));
  }
  return { stories: [...fresh, ...remnants], fromDelivered: remnants.length };
}

function deliveredById(state: ForYouV2State): Map<string, ForYouV2Story> {
  return new Map((state.delivered?.stories ?? []).map(story => [story.id, story]));
}

function shown(state: ForYouV2State): ForYouV2State {
  return { ...state, ...shownStories(state) };
}

function withFeed(state: ForYouV2State, searchId: string, change: Partial<ForYouV2Feed>): ForYouV2State {
  return { ...state, feeds: (state.feeds ?? []).map(feed => feed.search_id === searchId ? { ...feed, ...change } : feed) };
}

/** Fold one stream event into For You. An error ends it and keeps what was shown. */
export function applyForYouV2Event(state: ForYouV2State, event: ForYouV2Event): ForYouV2State {
  switch (event.type) {
    case 'feeds':
      return { ...state, feeds: event.feeds.map(feed => ({ ...feed, status: 'building' })) };
    case 'delivered':
      if (event.append) {
        if (!state.delivered) throw new Error('Delivery continuation arrived before its head');
        return shown({ ...state, delivered: { ...state.delivered, stories: [...state.delivered.stories, ...event.stories] } });
      }
      return shown({ ...state, delivered: { at: event.delivered_at, stories: event.stories } });
    case 'update': {
      const bodies = { ...state.bodies };
      for (const story of event.stories) bodies[story.id] = story;
      const delivered = event.patches?.length ? deliveredById(state) : null;
      for (const patch of event.patches ?? []) {
        const base = bodies[patch.id] ?? delivered?.get(patch.id);
        if (!base) throw new Error(`Patch for a story never sent: ${patch.id}`);
        bodies[patch.id] = { ...base, ...patch };
      }
      return shown({ ...state, bodies,
        order: event.append ? state.order : event.order,
        provisional: event.append ? state.provisional : Boolean(event.provisional) });
    }
    case 'feed_done':
      return withFeed(state, event.search_id, { status: 'done', stories: event.stories });
    case 'feed_error':
      return withFeed(state, event.search_id, { status: 'failed', detail: event.detail });
    case 'heartbeat':
      return state;
    case 'done':
      return shown({ ...state, done: true });
    case 'error':
      return { ...state, error: event.detail || 'For You failed', done: true };
  }
}

/** "Refreshing 3 of 8 feeds", or null once every feed has finished. */
export function buildProgress(feeds: ForYouV2Feed[] | null): string | null {
  if (feeds === null) return 'Loading your feeds…';
  const building = feeds.filter(feed => feed.status === 'building').length;
  return building ? `Refreshing ${building} of ${feeds.length} ${feeds.length === 1 ? 'feed' : 'feeds'}…` : null;
}

/** The failed feeds whose delivered stories are still shown. */
export function failedFeedsShownFromDelivery(state: ForYouV2State): Set<string> {
  const failed = new Set((state.feeds ?? []).filter(feed => feed.status === 'failed').map(feed => feed.search_id));
  const shown = new Set<string>();
  for (const story of state.stories.slice(state.stories.length - state.fromDelivered)) {
    for (const feed of story.for_you_searches ?? []) if (failed.has(feed.id)) shown.add(feed.id);
  }
  return shown;
}

/** Stories the page renders before the reader scrolls for more. */
export const STORIES_PER_PAGE = 100;
